import { supabase } from './api'

/**
 * Scorecards. Readable by everyone with a valid link — the floor team follows
 * along with these in meetings — and writable only by admins.
 *
 * The PDF bytes live in Postgres rather than Supabase Storage, so the personal
 * link token is the only access rule. See 0009's header for why.
 */

export interface ScorecardSummary {
  id: string
  label: string | null
  file_name: string
  byte_size: number
  uploaded_at: string
  uploaded_by: string | null
}

function fail(error: { code?: string; message?: string }): Error {
  if (error.code === '28000') {
    return new Error('This link is not recognised. Ask Scott for a new one.')
  }
  if (error.code === '42501') {
    return new Error(error.message || 'You do not have permission to do that.')
  }
  if (error.code === '22023') return new Error(error.message || 'That file was rejected.')
  return new Error(error.message || 'Something went wrong. Try again.')
}

export async function listScorecards(token: string): Promise<ScorecardSummary[]> {
  const { data, error } = await supabase.rpc('list_scorecards', { p_token: token })
  if (error) throw fail(error)
  return (data as ScorecardSummary[] | null) ?? []
}

/**
 * Fetch one scorecard.
 *
 * Returns BOTH the raw bytes and a blob URL, because they serve different jobs:
 *  - `bytes` go straight to PDF.js. Handing it a blob URL instead makes it fetch
 *    that URL, which `connect-src` blocks — the failure reads "Unexpected server
 *    response (0)". Passing the data avoids the request altogether.
 *  - `url` is only for the Download button.
 *
 * The caller owns the URL and must revoke it, or each view leaks a few hundred
 * KB for the life of the page.
 */
export async function fetchScorecardUrl(
  token: string,
  id: string,
): Promise<{ url: string; bytes: Uint8Array; fileName: string }> {
  const { data, error } = await supabase.rpc('get_scorecard', {
    p_token: token,
    p_id: id,
  })
  if (error) throw fail(error)

  const row = (data as
    | { file_name: string; mime_type: string; content_base64: string }[]
    | null)?.[0]
  if (!row) throw new Error('That scorecard could not be found.')

  // Postgres `encode(…, 'base64')` wraps at 76 characters, so the payload
  // arrives with newlines in it. atob() rejects those — strip whitespace first.
  const clean = row.content_base64.replace(/\s/g, '')
  const binary = atob(clean)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)

  // Blob takes its own copy, so `bytes` stays usable afterwards.
  const blob = new Blob([bytes], { type: row.mime_type || 'application/pdf' })
  return { url: URL.createObjectURL(blob), bytes, fileName: row.file_name }
}

/** Read a File into base64 without the `data:...;base64,` prefix. */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('Could not read that file.'))
    reader.onload = () => {
      const result = String(reader.result)
      const comma = result.indexOf(',')
      resolve(comma >= 0 ? result.slice(comma + 1) : result)
    }
    reader.readAsDataURL(file)
  })
}

export async function uploadScorecard(
  token: string,
  file: File,
  label: string | null,
): Promise<string> {
  const base64 = await fileToBase64(file)
  const { data, error } = await supabase.rpc('admin_upload_scorecard', {
    p_token: token,
    p_label: label,
    p_file_name: file.name,
    p_base64: base64,
  })
  if (error) throw fail(error)
  return data as string
}

export async function deleteScorecard(token: string, id: string): Promise<void> {
  const { error } = await supabase.rpc('admin_delete_scorecard', {
    p_token: token,
    p_id: id,
  })
  if (error) throw fail(error)
}

export function prettySize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}
